import React, { useMemo, useState } from "react";
import CommentComposer from "../../comments/explorer/CommentComposer";
import type { CommentNode } from "../../comments/explorer/types";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentDisplayText,
  getCommentScopeClass,
  getCommentScopeLabel,
} from "../../comments/explorer/commentText";
import type { DiscussionModel, DiscussionWorkspaceAdapter } from "../model/types";
import { filterRootThreadsForInbox, type WorkspaceThreadFilter } from "../model/discussionFilters";
import {
  getAnchorForSelectedComment,
  getBranchDepthLabel,
  getFirstChild,
  getNextSibling,
  getParentForComment,
  getPathForComment,
  getPreviousSibling,
  getRootForSelectedComment,
  getSafeSelectedCommentId,
  getSiblingInfoForComment,
  getSiblingsForComment,
} from "../model/discussionSelection";
import { AnchorContextCard } from "./AnchorContextCard";
import styles from "../CanonicalCommentWorkspace.module.css";

type BranchReaderProps = {
  model: DiscussionModel;
  selectedCommentId: number | null;
  adapter: DiscussionWorkspaceAdapter;
  onSelect: (commentId: number) => void;
};

function isAnchoredBranch(comment: CommentNode, model: DiscussionModel): boolean {
  let current: CommentNode | undefined = comment;
  const seen = new Set<number>();

  while (current && !seen.has(current.id)) {
    if (model.anchorsByRootCommentId.has(current.id)) return true;
    seen.add(current.id);
    current = typeof current.parentId === "number" ? model.byId.get(current.parentId) : undefined;
  }

  return false;
}

function getCommentRole(comment: CommentNode, model: DiscussionModel): string {
  if (model.anchorsByRootCommentId.has(comment.id)) return "anchored root";
  if (comment.parentId === null) return "general root";
  return isAnchoredBranch(comment, model) ? `anchored reply · depth ${comment.depth}` : `reply depth ${comment.depth}`;
}

const BranchCommentCard: React.FC<{
  comment: CommentNode;
  model: DiscussionModel;
  currentEditId?: number | null;
  isPrimary?: boolean;
  onSelect?: (commentId: number) => void;
}> = ({ comment, model, currentEditId, isPrimary = false, onSelect }) => {
  const scopeClass = getCommentScopeClass(comment, currentEditId);

  return (
    <article
      className={`${styles.branchCommentCard} ${isPrimary ? styles.branchCommentCardPrimary : ""} ${
        comment.isRemoved ? styles.branchCommentCardRemoved : ""
      }`}
    >
      <header className={styles.branchCommentHeader}>
        <div>
          <strong>{comment.user?.name || "Unknown user"}</strong>
          <span>{formatCommentTime(comment.createdAt)}</span>
        </div>
        <div className={styles.branchCommentBadges}>
          <span>{getCommentRole(comment, model)}</span>
          <span className={scopeClass}>{getCommentScopeLabel(comment, currentEditId)}</span>
        </div>
      </header>
      <p>{getCommentDisplayText(comment)}</p>
      <footer className={styles.branchCommentFooter}>
        <span>{comment.directReplyCount} direct replies</span>
        <span>{comment.totalReplyCount} total replies</span>
        {onSelect && (
          <button type="button" onClick={() => onSelect(comment.id)}>
            Select
          </button>
        )}
      </footer>
    </article>
  );
};

const BranchMetric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={styles.branchMetric}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const BranchQuickActions: React.FC<{
  parent: CommentNode | null;
  previousSibling: CommentNode | null;
  nextSibling: CommentNode | null;
  firstChild: CommentNode | null;
  onSelect: (commentId: number) => void;
}> = ({ parent, previousSibling, nextSibling, firstChild, onSelect }) => {
  if (!parent && !previousSibling && !nextSibling && !firstChild) return null;

  return (
    <div className={styles.branchQuickActions} aria-label="Branch quick jumps">
      {parent && (
        <button type="button" onClick={() => onSelect(parent.id)}>
          ↑ Parent
        </button>
      )}
      {previousSibling && (
        <button type="button" onClick={() => onSelect(previousSibling.id)}>
          ← Previous sibling
        </button>
      )}
      {nextSibling && (
        <button type="button" onClick={() => onSelect(nextSibling.id)}>
          Next sibling →
        </button>
      )}
      {firstChild && (
        <button type="button" onClick={() => onSelect(firstChild.id)}>
          First child ↓
        </button>
      )}
    </div>
  );
};

const BranchPathTrail: React.FC<{
  path: CommentNode[];
  selectedComment: CommentNode;
  onSelect: (commentId: number) => void;
}> = ({ path, selectedComment, onSelect }) => (
  <div className={styles.branchPathTrail} aria-label="Selected branch path">
    <div className={styles.branchSectionTitle}>
      <strong>Path to selected comment</strong>
      <span>{path.length} levels</span>
    </div>
    <div className={styles.branchPath}>
      {path.map((comment, index) => (
        <button
          key={comment.id}
          type="button"
          className={comment.id === selectedComment.id ? styles.branchPathCurrent : ""}
          onClick={() => onSelect(comment.id)}
        >
          {index === 0 ? "Root" : `Reply ${index}`} · {getCommentDisplayExcerpt(comment, 42)}
        </button>
      ))}
    </div>
  </div>
);

const BranchMapPanel: React.FC<{
  root: CommentNode | null;
  path: CommentNode[];
  selectedComment: CommentNode;
  siblings: CommentNode[];
  onSelect: (commentId: number) => void;
}> = ({ root, path, selectedComment, siblings, onSelect }) => {
  const childPreview = selectedComment.replies.slice(0, 5);

  return (
    <section className={styles.branchMapPanel} aria-label="Branch map">
      <div className={styles.branchSectionTitle}>
        <strong>Branch map</strong>
        <span>{selectedComment.totalReplyCount} nested</span>
      </div>

      {root && (
        <button
          type="button"
          className={`${styles.branchMapNode} ${root.id === selectedComment.id ? styles.branchMapNodeActive : ""}`}
          onClick={() => onSelect(root.id)}
        >
          <span>Root</span>
          <strong>{getCommentDisplayExcerpt(root, 72)}</strong>
        </button>
      )}

      <div className={styles.branchMapPathStack}>
        {path.map((comment, index) => (
          <button
            key={comment.id}
            type="button"
            className={`${styles.branchMapNode} ${comment.id === selectedComment.id ? styles.branchMapNodeActive : ""}`}
            style={{ marginLeft: `${Math.min(index, 5) * 10}px` }}
            onClick={() => onSelect(comment.id)}
          >
            <span>{index === 0 ? "Root" : `Level ${index}`}</span>
            <strong>{getCommentDisplayExcerpt(comment, 68)}</strong>
          </button>
        ))}
      </div>

      <div className={styles.branchMapTinyGroup}>
        <div className={styles.branchMapTinyTitle}>Same level</div>
        <div className={styles.branchMapDots}>
          {siblings.map((sibling) => (
            <button
              key={sibling.id}
              type="button"
              className={sibling.id === selectedComment.id ? styles.branchMapDotActive : ""}
              title={getCommentDisplayExcerpt(sibling, 90)}
              aria-label={`Select sibling comment ${sibling.id}`}
              onClick={() => onSelect(sibling.id)}
            />
          ))}
        </div>
      </div>

      <div className={styles.branchMapTinyGroup}>
        <div className={styles.branchMapTinyTitle}>Direct children</div>
        {childPreview.length ? (
          <div className={styles.branchMapChildList}>
            {childPreview.map((child) => (
              <button key={child.id} type="button" onClick={() => onSelect(child.id)}>
                <span>↓</span>
                <strong>{getCommentDisplayExcerpt(child, 76)}</strong>
              </button>
            ))}
            {selectedComment.replies.length > childPreview.length && (
              <em>+{selectedComment.replies.length - childPreview.length} more direct replies</em>
            )}
          </div>
        ) : (
          <div className={styles.branchEmptyInline}>No child comments yet.</div>
        )}
      </div>
    </section>
  );
};


const FILTERS: { value: WorkspaceThreadFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "anchored", label: "Anchored" },
  { value: "general", label: "General" },
];

const BranchThreadInbox: React.FC<{
  model: DiscussionModel;
  selectedComment: CommentNode;
  rootComment: CommentNode | null;
  onSelect: (commentId: number) => void;
}> = ({ model, selectedComment, rootComment, onSelect }) => {
  const [filter, setFilter] = useState<WorkspaceThreadFilter>("all");
  const [query, setQuery] = useState("");

  const filteredRoots = useMemo(
    () => filterRootThreadsForInbox(model.roots, model.anchoredRootCommentIds, filter, query),
    [filter, model.anchoredRootCommentIds, model.roots, query],
  );

  return (
    <section className={styles.branchThreadInbox} aria-label="Thread inbox">
      <div className={styles.branchSectionTitle}>
        <strong>Thread inbox</strong>
        <span>{filteredRoots.length}</span>
      </div>

      <div className={styles.branchInboxFilters} aria-label="Thread filters">
        {FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={filter === item.value ? styles.branchInboxFilterActive : ""}
            onClick={() => setFilter(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <label className={styles.branchInboxSearch}>
        <span>Search this discussion</span>
        <input
          type="search"
          value={query}
          placeholder="Author or comment text…"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      <div className={styles.branchInboxList}>
        {filteredRoots.length ? (
          filteredRoots.map((thread) => {
            const isSelectedRoot = rootComment?.id === thread.id;
            const isSelectedComment = selectedComment.id === thread.id;
            const isAnchored = model.anchoredRootCommentIds.has(thread.id);

            return (
              <button
                key={thread.id}
                type="button"
                className={`${isSelectedRoot ? styles.branchInboxThreadActive : ""} ${
                  isSelectedComment ? styles.branchInboxThreadSelected : ""
                }`}
                onClick={() => onSelect(thread.id)}
              >
                <span>{isAnchored ? "Anchored thread" : "General thread"}</span>
                <strong>{getCommentDisplayExcerpt(thread, 92)}</strong>
                <em>
                  {thread.totalReplyCount} nested · {formatCommentTime(thread.createdAt)}
                </em>
              </button>
            );
          })
        ) : (
          <div className={styles.branchEmptyInline}>No threads match this filter.</div>
        )}
      </div>
    </section>
  );
};

export const BranchReader: React.FC<BranchReaderProps> = ({
  model,
  selectedCommentId,
  adapter,
  onSelect,
}) => {
  const safeSelectedId = getSafeSelectedCommentId(model.byId, model.roots, selectedCommentId);
  const selectedComment = safeSelectedId === null ? null : model.byId.get(safeSelectedId) ?? null;
  const rootComment = getRootForSelectedComment(model.byId, selectedComment);
  const selectedAnchor = getAnchorForSelectedComment(
    model.byId,
    model.anchorsByRootCommentId,
    selectedComment,
  );
  const path = getPathForComment(model.byId, selectedComment);
  const parent = getParentForComment(model.byId, selectedComment);
  const siblings = getSiblingsForComment(model.roots, model.byId, selectedComment);
  const siblingInfo = getSiblingInfoForComment(model.roots, model.byId, selectedComment);
  const previousSibling = getPreviousSibling(model.roots, model.byId, selectedComment);
  const nextSibling = getNextSibling(model.roots, model.byId, selectedComment);
  const firstChild = getFirstChild(selectedComment);
  const currentEditId = selectedComment?.editId ?? null;

  if (!model.all.length) {
    return (
      <div className={styles.branchEmptyState}>
        <strong>No comments yet.</strong>
        <span>Use the composer above to start a general discussion for this Gist.</span>
        <span>For anchored discussion, select text in the Gist body and create a comment from that selection.</span>
      </div>
    );
  }

  if (!selectedComment) {
    return (
      <div className={styles.branchEmptyState}>
        <strong>Select a thread.</strong>
        <span>The Branch reader will show its path, siblings, direct replies, and anchor context.</span>
      </div>
    );
  }

  return (
    <div className={styles.branchReader}>
      <div className={styles.branchMainColumn}>
        <section className={styles.branchOrientationPanel} aria-label="Selected branch orientation">
          <BranchMetric label="Position" value={`${siblingInfo.index + 1} / ${Math.max(1, siblingInfo.count)}`} />
          <BranchMetric label="Depth" value={getBranchDepthLabel(selectedComment)} />
          <BranchMetric label="Direct replies" value={selectedComment.directReplyCount} />
          <BranchMetric label="Nested replies" value={selectedComment.totalReplyCount} />
        </section>

        <BranchPathTrail path={path} selectedComment={selectedComment} onSelect={onSelect} />

        <BranchQuickActions
          parent={parent}
          previousSibling={previousSibling}
          nextSibling={nextSibling}
          firstChild={firstChild}
          onSelect={onSelect}
        />

        <AnchorContextCard
          anchor={selectedAnchor}
          onResolve={adapter.resolveAnchor}
          onReopen={adapter.reopenAnchor}
        />

        <BranchCommentCard
          comment={selectedComment}
          model={model}
          currentEditId={currentEditId}
          isPrimary
        />

        <section className={styles.branchChildrenSection} aria-label="Direct replies">
          <div className={styles.branchSectionTitle}>
            <strong>Direct replies</strong>
            <span>{selectedComment.replies.length}</span>
          </div>
          {selectedComment.replies.length ? (
            <div className={styles.branchReplyList}>
              {selectedComment.replies.map((reply) => (
                <BranchCommentCard
                  key={reply.id}
                  comment={reply}
                  model={model}
                  currentEditId={currentEditId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : (
            <div className={styles.branchEmptyInline}>No direct replies yet.</div>
          )}
        </section>

        {adapter.canComment && adapter.replyToComment ? (
          <div className={styles.branchReplyComposer}>
            <div className={styles.branchReplyComposerHeader}>
              <strong>Reply inside this branch</strong>
              <span>Your reply will stay attached to the currently selected comment.</span>
            </div>
            <CommentComposer
              label="Reply to selected comment"
              placeholder="Write a focused reply to this branch…"
              onSubmit={(content) => adapter.replyToComment?.(selectedComment.id, content) || Promise.resolve()}
            />
          </div>
        ) : (
          <div className={styles.branchReplyLocked}>
            <strong>Reply is locked</strong>
            <span>Log in to reply to this selected comment. You can still navigate the branch and read every child reply.</span>
          </div>
        )}
      </div>

      <aside className={styles.branchSideRail} aria-label="Branch navigation">
        <BranchThreadInbox
          model={model}
          selectedComment={selectedComment}
          rootComment={rootComment}
          onSelect={onSelect}
        />

        <BranchMapPanel
          root={rootComment}
          path={path}
          selectedComment={selectedComment}
          siblings={siblings}
          onSelect={onSelect}
        />

        <section className={styles.branchSiblingPanel} aria-label="Sibling comments">
          <div className={styles.branchSectionTitle}>
            <strong>Siblings</strong>
            <span>{siblings.length}</span>
          </div>
          <div className={styles.branchSiblingList}>
            {siblings.map((sibling) => {
              const siblingInPath = path.some((comment) => comment.id === sibling.id);
              return (
                <button
                  key={sibling.id}
                  type="button"
                  className={`${sibling.id === selectedComment.id ? styles.branchSiblingActive : ""} ${
                    siblingInPath ? styles.branchSiblingInPath : ""
                  }`}
                  onClick={() => onSelect(sibling.id)}
                >
                  <span>{sibling.user?.name || "Unknown user"}</span>
                  <strong>{getCommentDisplayExcerpt(sibling, 82)}</strong>
                  <em>{sibling.totalReplyCount} nested</em>
                </button>
              );
            })}
          </div>
        </section>
      </aside>
    </div>
  );
};
